<template>
  <ThePage :title="i18n(translations.title)">
    <template #pageTitle>
      {{ i18n(translations.title) }}
    </template>

    <template #pageContent>
      <p class="mb-32">
        <span>
          {{ i18n(translations.subtitle) }}
        </span>
        <RevLink
          :to="{ name: ROUTE_NAMES.LISTINGS.PRODUCT.CREATE }"
          :tracking="{
            page_type: 'listings_create_product_search',
            zone: 'add_new_item_catalog',
          }"
        >
          {{ i18n(translations.addNewProductLink) }}
        </RevLink>
      </p>

      <div class="space-y-32">
        <RevInputText
          id="search"
          v-model="filtersValues.search"
          class="w-full"
          :icon="IconSearch"
          :label="i18n(translations.formFieldSearchLabel)"
          :placeholder="i18n(translations.formFieldSearchPlaceholder)"
          type="text"
          @input="handleSearchInputChange"
        />

        <NoResults v-if="shouldShowNoResults" />

        <NoSearch v-if="shouldShowNoSearch" />

        <RevLoadingScreen
          v-if="shouldShowLoadingScreen"
          :text="i18n(translations.loadingScreenTitle)"
        />

        <RevInfoBlock
          v-if="shouldShowError && apiError"
          :content="apiError.detail"
          :icon="IconWarning"
          :title="apiError.title || i18n(translations.errorTitle)"
          variant="danger"
        />

        <section
          v-if="data && shouldShowResults"
          :aria-label="i18n(translations.sectionResultsTitle)"
          class="flex flex-row space-x-32"
          role="contentinfo"
        >
          <Facets
            v-model="facetsValues"
            class="w-[250px]"
            :facets="data.facets"
          />

          <div class="flex-1">
            <ResultsCounter
              class="mb-20"
              :count="data.totalProductsCount"
              :search="sanitizedFiltersValues.search"
            />
            <ul class="list-none space-y-24">
              <li v-for="(product, index) in data.products" :key="product.id">
                <ProductCard :product :product-position="index + 1" />
              </li>
            </ul>
            <Pagination
              class="mt-56"
              :current-page="sanitizedFiltersValues.page || DEFAULT_PAGE_NUMBER"
              :page-count
              @click="handlePaginationClick"
            />
          </div>
        </section>
      </div>
    </template>
  </ThePage>
</template>

<script setup lang="ts">
import { navigateTo, useFilters, useRoute } from '#imports'
import { computed, ref, watch } from 'vue'

import { HttpApiError, catalogAccessAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconSearch } from '@ds/icons/IconSearch'
import { IconWarning } from '@ds/icons/IconWarning'

import { ROUTE_NAMES } from '~/constants/routes'

import {
  DEBOUNCE_INPUT_SEARCH_DELAY,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
} from './ProductSearch.constants'
import translations from './ProductSearch.translations'
import {
  getAlgoliaHitsCount,
  getAlgoliaPageNumber,
  getAlgoliaPagesCount,
  getFacetsValuesFromQuery,
} from './ProductSearch.utils'
import Facets from './components/Facets/Facets.vue'
import NoResults from './components/NoResults/NoResults.vue'
import NoSearch from './components/NoSearch/NoSearch.vue'
import ProductCard from './components/ProductCard/ProductCard.vue'
import ResultsCounter from './components/ResultsCounter/ResultsCounter.vue'

const tracking = useTracking()
const route = useRoute()
const i18n = useI18n()

const initialFiltersValues = { search: '', page: DEFAULT_PAGE_NUMBER }

const facetsValues = ref(getFacetsValuesFromQuery(route.query))

const { sanitizedFiltersValues, filtersValues, submitFilters, setCurrentPage } =
  useFilters(initialFiltersValues)

const body = computed(() => ({
  query: sanitizedFiltersValues.value.search,
  page: getAlgoliaPageNumber(sanitizedFiltersValues.value.page),
  facets_publication_state: 'published',
  hitsPerPage: DEFAULT_PAGE_SIZE,
  ...getFacetsValuesFromQuery(route.query),
}))

const { error, data, status, refresh } = useHttpFetch(
  catalogAccessAPI.postProductsSellerSearch,
  {
    body,
    immediate: Boolean(sanitizedFiltersValues.value.search),
    transform: (payload) => ({
      products: payload.hits,
      facets: payload.facets,
      totalProductsCount: getAlgoliaHitsCount(payload),
      totalProductsPagesCount: getAlgoliaPagesCount(payload),
    }),
    dedupe: 'cancel',
    server: false,
    watch: false,
  },
)

const pageCount = computed(() => data.value?.totalProductsPagesCount ?? 0)

watch(facetsValues, async () => {
  await navigateTo({
    ...route,
    query: {
      search: route.query.search,
      page: route.query.page,
      ...facetsValues.value,
    },
    replace: true,
  })
})

watch(
  body,
  () => {
    if (body.value.query) {
      refresh()
    }
  },
  { immediate: true },
)

const shouldShowError = computed(() => status.value === 'error')
const shouldShowLoadingScreen = computed(() => status.value === 'pending')
const shouldShowResults = computed(
  () => status.value === 'success' && Boolean(data.value?.products?.length),
)
const shouldShowNoResults = computed(
  () => status.value === 'success' && !data.value?.products?.length,
)
const shouldShowNoSearch = computed(
  () => status.value === 'idle' && !sanitizedFiltersValues.value.search,
)

const apiError = computed(() =>
  error.value ? HttpApiError.fromAnyError(error.value) : null,
)

const handleSearchInputChange = useDebounceFn(
  async function debouncedHandleInputTextChange() {
    await submitFilters()

    tracking.trackFormSubmit({
      name: sanitizedFiltersValues.value.search || '',
      page_type: 'listings_create_product_search',
      zone: 'listings_create_searchbar',
    })
  },
  DEBOUNCE_INPUT_SEARCH_DELAY,
)

async function handlePaginationClick({ page }: { page: number }) {
  await setCurrentPage(page)
}
</script>
